/**
 * Drag-to-reorder that works with a finger, a stylus and a mouse.
 *
 * This replaces HTML5 drag-and-drop, which is a mouse-only API in practice: touch input
 * does not produce `dragstart`, and a browser's device-emulation mode dispatches touch
 * events too, so reordering was dead on a phone and dead in the emulator that would have
 * shown it. It had never worked there — stacking the rows for narrow screens only made the
 * grip visible enough to try.
 *
 * Pointer events cover mouse, pen and touch in one path, so there is no second
 * implementation to keep in step with the first.
 *
 * The keyboard path lives here too, for the same reason: three ways to move a row is two
 * too many places for the gap arithmetic to disagree with itself.
 */

import { useCallback, useId, useState } from "react";

export type DropEdge = "top" | "bottom";

/**
 * The gap a pointer at this height is aiming at: the one above this row, or the one below.
 * Gaps are numbered like `insertAt` — 0 is above the first row, `count` is below the last.
 */
function gapAt(index: number, box: DOMRect, clientY: number): number {
  return clientY < box.top + box.height / 2 ? index : index + 1;
}

export function useRowReorder(
  onReorder?: (from: number, insertAt: number) => void,
  /** How many rows are in the list, so the gap below the last one can be drawn. */
  count = 0,
) {
  // Scopes the hit test to this list's own rows, so two reorderable tables on one page
  // cannot drop into each other.
  const group = useId();
  const [from, setFrom] = useState<number | null>(null);
  /**
   * The gap the drop would land in — not a row and an edge.
   *
   * Storing it as a row plus an edge meant one gap had two spellings: "below row 2" and
   * "above row 3" are the same place, but they drew the line on different sides of the
   * border between them, so it twitched as the pointer crossed the midpoint. A gap is one
   * thing, so it gets one representation and one place to draw.
   */
  const [gap, setGap] = useState<number | null>(null);

  const reset = useCallback(() => {
    setFrom(null);
    setGap(null);
  }, []);

  const commit = useCallback(() => {
    // Already the insertion point `reorder` expects: 0 is above the first row, `count` is
    // below the last, which is what makes the bottom of the list reachable.
    if (from !== null && gap !== null && onReorder) onReorder(from, gap);
    reset();
  }, [from, gap, onReorder, reset]);

  const gripProps = (index: number) => ({
    "aria-label": "Reorder - drag, or use the arrow keys",
    // Withheld mid-drag: the native tooltip pops up over the row you are trying to aim at.
    title: from === null ? "Drag to reorder, or use the arrow keys" : undefined,
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      if (!onReorder) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      // Stops a touch drag scrolling the page instead of moving the row. `touch-action:
      // none` on `.grip` is the other half of this; neither works alone.
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setFrom(index);
      setGap(index);
    },

    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      if (from === null) return;
      // A hit test against the document, which pointer capture does not affect — capture
      // decides who receives the event, not what is underneath it.
      const row = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>(`[data-reorder="${CSS.escape(group)}"]`);
      if (!row?.dataset.rowIndex) return;

      setGap(gapAt(Number(row.dataset.rowIndex), row.getBoundingClientRect(), event.clientY));
    },

    onPointerUp: commit,
    onPointerCancel: reset,
    onLostPointerCapture: reset,

    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (!onReorder) return;
      // Gaps again: up is the gap above the previous row, down is the gap below the next.
      if (event.key === "ArrowUp" && index > 0) {
        event.preventDefault();
        onReorder(index, index - 1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        onReorder(index, index + 2);
      }
    },
  });

  /**
   * Which side of which row draws the line for the current gap.
   *
   * Every gap but the last is the *top* of the row below it, so crossing from one row's
   * lower half into the next row's upper half changes nothing on screen. Only the gap past
   * the final row has no row beneath it to hang from, and that one draws on the bottom of
   * the last row.
   */
  const dropClass = (index: number): DropEdge | null => {
    if (from === null || gap === null) return null;
    if (gap === index) return "top";
    if (gap >= count && index === count - 1) return "bottom";
    return null;
  };

  /** Props for the row itself: the hit-test hook, and the line showing where it will land. */
  const rowProps = (index: number) => {
    const edge = dropClass(index);
    return {
      "data-reorder": group,
      "data-row-index": index,
      className:
        [from === index ? "dragging" : "", edge ? `drop-${edge}` : ""]
          .filter(Boolean)
          .join(" ") || undefined,
    };
  };

  return { dragging: from !== null, gripProps, rowProps };
}
